import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type ApiError } from '@whinerp/client';
import {
  Button,
  EmptyState,
  ErrorState,
  FIELD_RENDERERS,
  Icon,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
  useToast,
} from '@whinerp/ui';
import { FIELD_TYPES, type FieldDefinition, type FieldType, type FieldValue } from '@whinerp/contracts/fields';
import { useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-33 Field editor.
 *
 * The type picker is icon cards rather than a dropdown, because choosing a
 * field type is the one decision here that cannot be changed later without
 * touching data - and a dropdown makes twenty-one options look like a list of
 * synonyms.
 *
 * The preview is the real renderer. It is the same `FIELD_RENDERERS` entry the
 * record page will use, given the values being typed - so what an
 * administrator sees is what a rep will see, rather than a drawing of it.
 */

const ICONS: Partial<Record<FieldType, string>> = {
  text: 'TextT',
  textarea: 'AlignLeft',
  email: 'Envelope',
  phone: 'Phone',
  url: 'Link',
  number: 'Hash',
  decimal: 'Hash',
  currency: 'Banknote',
  percent: 'Percent',
  date: 'Calendar',
  datetime: 'Clock',
  boolean: 'ToggleLeft',
  picklist: 'List',
  multipicklist: 'ListChecks',
  lookup: 'Link2',
  user: 'User',
  file: 'Paperclip',
  tags: 'Tag',
  address: 'MapPin',
  formula: 'Sigma',
  rollup: 'Sigma',
};

function FieldEditorScreen(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();
  const objectKey = params['obj'] ?? 'contact';

  const [type, setType] = useState<FieldType>('text');
  const [label, setLabel] = useState('');
  const [fieldKey, setFieldKey] = useState('');
  const [preview, setPreview] = useState<FieldValue>('');

  const fields = useQuery({
    queryKey: ['object-layout', objectKey],
    queryFn: () => api.getObjectLayout({ path: { objectKey } }),
  });

  const create = useMutation({
    mutationFn: () =>
      api.createCustomField({
        path: { objectKey },
        body: {
          fieldKey: fieldKey || suggestKey(label),
          label,
          type,
          ...(type === 'picklist' || type === 'multipicklist'
            ? { options: [{ value: 'one', label: 'One' }] }
            : {}),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['object-layout', objectKey] });
      toast.show({ tone: 'success', title: t('state.saved'), body: label });
      navigate(`/setup/objects/${objectKey}/layout`);
    },
    onError: (error: ApiError) =>
      // Name collisions and reserved words explained inline, in the server's
      // own words: it is the only thing that knows which names are taken.
      toast.show({ tone: 'error', title: t('state.error'), body: error.message }),
  });

  const definition: FieldDefinition = {
    key: fieldKey || 'preview',
    label: label || t('setup.field.new'),
    type,
    required: false,
    isCustom: true,
    sortOrder: 0,
    options: [
      { value: 'one', label: 'One' },
      { value: 'two', label: 'Two' },
    ],
  };
  const renderer = FIELD_RENDERERS[type];

  return (
    <div data-component="FieldEditor" className="mx-auto flex w-full max-w-panel-wide flex-col gap-4">
      <PageHeading>{t('setup.field.new')}</PageHeading>

      <Panel data-region="type">
        <div className="flex flex-col gap-2">
          <SectionLabel>{t('setup.field.type')}</SectionLabel>
          {/* Icon cards. Twenty-one options in a dropdown look like synonyms. */}
          <div
            role="radiogroup"
            aria-label={t('setup.field.type')}
            className="grid grid-cols-3 gap-2 sm:grid-cols-4"
          >
            {FIELD_TYPES.map((candidate) => (
              <button
                key={candidate}
                type="button"
                role="radio"
                data-field-type={candidate}
                aria-checked={type === candidate}
                onClick={() => setType(candidate)}
                className={
                  type === candidate
                    ? 'flex min-h-touch flex-col items-center gap-1 rounded-control bg-bronze-tint p-2 text-meta text-bronze-text'
                    : 'flex min-h-touch flex-col items-center gap-1 rounded-control bg-sunken p-2 text-meta text-ink-600 hover:text-ink'
                }
              >
                <Icon name={ICONS[candidate] ?? 'TextT'} size="inline" decorative />
                {candidate}
              </button>
            ))}
          </div>
        </div>
      </Panel>

      <Panel data-region="details">
        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-dense text-ink">
            {t('setup.field.name')}
            <input
              data-field-label
              value={label}
              onChange={(event) => {
                setLabel(event.target.value);
                if (!fieldKey) setFieldKey('');
              }}
              className="h-input rounded-control border border-rule bg-surface px-3 text-body text-ink"
            />
          </label>
          <label className="flex flex-col gap-1 text-dense text-ink">
            {t('setup.field.key')}
            <input
              data-field-key
              value={fieldKey || suggestKey(label)}
              onChange={(event) => setFieldKey(event.target.value)}
              className="h-input rounded-control border border-rule bg-surface px-3 font-mono text-body text-ink"
            />
          </label>
        </div>
      </Panel>

      <Panel data-region="preview">
        <div className="flex flex-col gap-2">
          <SectionLabel>{t('setup.field.preview')}</SectionLabel>
          {/* The real renderer, not a drawing of one. It is given a label of
              its own: a preview that a screen reader announces as an unnamed
              input is a preview of something inaccessible. */}
          <label htmlFor="field-preview" className="text-dense text-ink">
            {label || t('setup.field.new')}
          </label>
          <renderer.Edit
            id="field-preview"
            field={definition}
            value={preview}
            labels={{
              yes: t('field.yes'),
              no: t('field.no'),
              download: t('field.download'),
              more: (count) => t('field.more', { values: { count: String(count) } }),
              choose: t('field.choose'),
              clear: t('field.clear'),
            }}
            onChange={setPreview}
          />
        </div>
      </Panel>

      <div className="flex items-center gap-2">
        <Button
          intent="primary"
          data-create-field
          disabled={create.isPending || label.trim() === ''}
          onClick={() => create.mutate()}
        >
          {t('setup.field.create')}
        </Button>
        <Button intent="ghost" onClick={() => navigate(`/setup/objects/${objectKey}/layout`)}>
          {t('action.cancel')}
        </Button>
        {fields.isPending ? <Skeleton className="h-4 w-block-sm" /> : null}
      </div>
    </div>
  );
}

/** A label turned into an API name, which a person can then change. */
function suggestKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function FieldEditorEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="TextT"
      title={t('setup.field.new')}
      body={t('setup.field.type')}
      primary={{ label: t('empty.action.chooseType'), icon: 'TextT', href: '/setup' }}
    />
  );
}

function FieldEditorLoading(): JSX.Element {
  const t = useT();
  return (
    <div
      className="mx-auto flex w-full max-w-panel-wide flex-col gap-4"
      role="status"
      aria-live="polite"
      aria-busy
    >
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-md" />
      <Skeleton className="h-block-md w-full" />
    </div>
  );
}

function FieldEditorError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="The field editor could not be loaded"
      body={error instanceof Error ? error.message : 'It did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-33',
  route: '/setup/objects/:obj/fields/new',
  surface: 'slide-over',
  chrome: 'bare',
  title: 'Field editor',
  features: ['F-009'],
  entitlement: 'F-009',
  reachedBy: [{ kind: 'screen', from: 'SC-32', how: 'New field in the layout builder' }],
  components: [],
  elements: ['FieldEditor'],
  sampleParams: { obj: 'contact' },
  states: {
    empty: FieldEditorEmpty,
    loading: FieldEditorLoading,
    error: FieldEditorError,
  },
  component: FieldEditorScreen,
});

export default screen;
